import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { EntityData, GroupDetails, OUDetails } from '../types';
import {
    Button,
    ButtonStyles,
    EmptyStateIcons,
    Search,
    SeverityLevel,
    Table,
    TableColumn,
    Tabs
} from '@silverfort/sf-components';
import { getUserState, isUserPermitted } from '../../utils';
import { ROLES } from '@silverfort-nodelibs/common/utils/consts';
import { User, UserSession } from '../../../api/types/UserData';
import { AD_ENTITY_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import { usePricingModulesState } from '../../../context/contexts/PricingModuleContext';
import { InventoryEntity } from '../../Inventory/types';

const createPropertyRow = (title, data) => {
    if (data === undefined || !title) {
        return <></>;
    }

    return (
        <div className="flex w-full items-center">
            <div className="h-4 w-[200px] text-sf-gray-800 font-medium text-13">{title}</div>
            <div className="h-4 text-sf-gray-500 text-13">{data || 'N/A'}</div>
        </div>
    );
};

const DEFAULT_RISK_LEVEL = 1;

type UserDetails = Required<OUDetails>['users'][number];

const buildUserColumns = (pricingModulesState) => {
    const columns: TableColumn<UserDetails>[] = [
        { columnKey: 'display_name', title: 'Name' },
        { columnKey: 'identifier', title: 'User name' }
    ];

    if (pricingModulesState?.ispm?.anyEnabled || pricingModulesState?.itdr?.anyEnabled) {
        columns.push({
            columnKey: 'risk_level',
            title: 'Risk level',
            render: ({ risk_level }) => (
                <div className="w-9">
                    <SeverityLevel severity={risk_level || DEFAULT_RISK_LEVEL} />
                </div>
            )
        });
    }

    return columns;
};

type UserTableProps = {
    entityData: EntityData;
    onEntityClick: GetEntityClick;
};

const SortUserGroupTable: FC<UserTableProps> = ({ entityData, onEntityClick }) => {
    const { pricingModulesState } = usePricingModulesState();

    const [orderBy, setOrderBy] = useState<keyof UserDetails | undefined>();
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | undefined>();

    const onSortChange = useCallback((orderBy: keyof UserDetails, sortDirection: 'asc' | 'desc') => {
        setOrderBy(orderBy);
        setSortDirection(sortDirection);
    }, []);

    const sortedUsers = useMemo(() => {
        const users = (entityData.details as OUDetails)?.users ?? [];

        if (!orderBy || !sortDirection) return users;

        const sorted = users.map((user) => ({ ...user, risk_level: user.risk_level ?? DEFAULT_RISK_LEVEL }));
        const field = sorted[0][orderBy];

        if (typeof field === 'string') {
            sorted.sort((a, b) => (a[orderBy] as string).localeCompare(b[orderBy] as string));
        } else if (typeof field === 'number' || typeof field === 'boolean' || field instanceof Date) {
            sorted.sort((a, b) => (a[orderBy] as number) - (b[orderBy] as number));
        }

        if (sortDirection === 'desc') sorted.reverse();

        return sorted;
    }, [entityData?.details, orderBy, sortDirection]);

    return (
        <Table
            onRowClick={(item) => onEntityClick(item.guid, item.display_name, AD_ENTITY_TYPES.USER)}
            columns={buildUserColumns(pricingModulesState)}
            bodyHeight={460}
            rows={sortedUsers}
            EmptyStateProps={{ title: 'No nested users', icon: EmptyStateIcons.NoItems }}
            onSortChange={onSortChange}
            defaultSortColumn={orderBy}
            defaultSortDirection={sortDirection}
        />
    );
};

const renderPropertiesTab = (entityData: EntityData) => {
    const details = entityData.details as GroupDetails;
    return (
        <div className="border h-full w-full border-sf-gray-100 py-2 px-6 rounded">
            <div className="flex h-full flex-col gap-4">
                {createPropertyRow('Domain', details.domain_name)}
                {createPropertyRow('Name', details.name)}
                {createPropertyRow('Guid', details.guid)}
                {createPropertyRow('Path', details.path)}
                {createPropertyRow('SID', details.sid)}
            </div>
        </div>
    );
};

const renderTabData = (
    entityData: any,
    selectedTab: number,
    onEntityClick: GetEntityClick,
    tableSearch: string,
    setTableSearch: (value: string) => void,
    user: User | undefined
) => {
    switch (selectedTab) {
        case 0:
            return renderPropertiesTab(entityData);
        case 1:
            return <SortUserGroupTable entityData={entityData} onEntityClick={onEntityClick} />;
        case 2:
            return (
                <Table
                    columns={[
                        { columnKey: 'name', title: 'Name' },
                        { columnKey: 'sid', title: 'SID' },
                        { columnKey: 'path', title: 'Path' }
                    ]}
                    bodyHeight={460}
                    rows={entityData.details.groups}
                    onRowClick={(item: InventoryEntity) => onEntityClick(item.guid, item.name, AD_ENTITY_TYPES.GROUP)}
                    EmptyStateProps={{ title: 'No nested groups', icon: EmptyStateIcons.NoItems }}
                />
            );

        case 3:
            return (
                <Table
                    columns={[
                        { columnKey: 'display_name', title: 'Name' },
                        { columnKey: 'operating_system', title: 'Operating system' }
                    ]}
                    bodyHeight={460}
                    rows={entityData.details.resources}
                    onRowClick={(item: InventoryEntity) =>
                        onEntityClick(item.guid, item.display_name, AD_ENTITY_TYPES.SERVER)
                    }
                    EmptyStateProps={{ title: 'No nested resources', icon: EmptyStateIcons.NoItems }}
                />
            );
        case 4:
            return (
                <>
                    <div className="flex items-center justify-end">
                        <Search placeholder="Search policy" border text={tableSearch} onChange={setTableSearch} />
                    </div>
                    <Table
                        disableHighlightedFirstColumn
                        rowClassName="!cursor-default"
                        columns={[
                            { columnKey: 'name', title: 'Name' },
                            {
                                columnKey: 'id',
                                title: '',
                                render: (row) => {
                                    return isUserPermitted(user, 'policies', ROLES.VIEWER.value) ? (
                                        <div className="flex items-center justify-end">
                                            <a
                                                id={'manage_identity_manage_policy_' + row.id}
                                                href={`/Policy#${row.id}`}
                                                rel="noopener noreferrer"
                                                target="_blank"
                                                className="decoration-transparent !cursor-pointer">
                                                <Button
                                                    text="Manage policy"
                                                    onClick={() => {}}
                                                    buttonStyle={ButtonStyles.TertiaryBorder}
                                                />
                                            </a>
                                        </div>
                                    ) : (
                                        <></>
                                    );
                                }
                            }
                        ]}
                        rows={
                            entityData.rules?.filter((rule) =>
                                rule.name.toLowerCase().includes(tableSearch?.toLowerCase())
                            ) || []
                        }
                        EmptyStateProps={{ title: 'No Active Policies', icon: EmptyStateIcons.NoItems }}
                    />
                </>
            );
        default:
            break;
    }
};

type GetEntityClick = (guid?: string, identifier?: string, type?: number) => void;

const GroupDataTable: FC<{
    entityData: EntityData;
    adType: number;
    onEntityClick: GetEntityClick;
}> = ({ entityData, adType, onEntityClick }) => {
    const [selectedTab, setSelectedTab] = useState<number>(0);
    const [tableSearch, setTableSearch] = useState('');
    const [user, setUser] = useState<User | undefined>();

    useEffect(() => {
        setTableSearch('');
        getUserState().then((userData) => userData && setUser((userData as UserSession).user));
    }, [selectedTab]);

    const GROUP_DATA_TABS = [
        { value: 0, name: 'Properties' },
        { value: 1, name: `Users (${(entityData.details as GroupDetails)?.usersCount || 0})` },
        { value: 2, name: `Groups (${(entityData.details as GroupDetails)?.groupsCount || 0})` },
        { value: 3, name: `Resources (${(entityData.details as GroupDetails)?.resourcesCount || 0})` },
        { value: 4, name: `Active policies (${entityData.rules?.length || 0})` }
    ];

    return (
        <div className="h-full w-full flex flex-col gap-4 px-6 py-4">
            <Tabs selectedTab={selectedTab} onClick={setSelectedTab} tabs={GROUP_DATA_TABS} />
            {renderTabData(entityData, selectedTab, onEntityClick, tableSearch, setTableSearch, user)}
        </div>
    );
};

export default GroupDataTable;
